import { useState } from 'react';
import { YStack } from 'tamagui';

import {
    BackButton,
    BackButtonIconType,
} from '../../components/buttons/BackButton';
import {
    Button,
    ButtonSize,
    ButtonVariant,
} from '../../components/buttons/Button';
import { DollarInput } from '../../components/inputs/DollarInput';
import { Input } from '../../components/inputs/Input';
import { KeyboardCompatibleBottomSheetTextInput } from '../../components/inputs/KeyboardCompatibleBottomSheetTextInput';
import { KeyboardCompatibleBottomSheet } from '../../components/sheet/KeyboardCompatibleBottomSheet';
import { PremiumInfoSheet } from '../../components/sheet/PremiumInfoSheet';
import { Sheet } from '../../components/sheet/Sheet';
import { TallSheet } from '../../components/sheet/TallSheet';
import {
    BodyLargeRegular,
    BodyMediumRegular,
    BodySmallRegular,
    FootnoteRegular,
} from '../../components/text/Typography';

/**
 * Inner component for the keyboard dollar input sheet.
 * IMPORTANT: Local state must be managed in a separate component so the whole sheet
 * doesn't re-render on state change (if it does, it doesn't account for the keyboard size).
 */
const KeyboardDollarInputContent = ({
    onConfirm,
}: {
    onConfirm: (value: string) => void;
}) => {
    const [dollarValue, setDollarValue] = useState('');

    return (
        <YStack alignItems="center" gap={16}>
            <BodyMediumRegular textAlign="center">
                This sheet uses DollarInput as the component prop for
                KeyboardCompatibleBottomSheetTextInput.
            </BodyMediumRegular>
            <KeyboardCompatibleBottomSheetTextInput
                component={DollarInput}
                placeholder="$0.00"
                value={dollarValue}
                onChangeText={setDollarValue}
                allowDecimals={true}
                width="100%"
            />
            <FootnoteRegular color="$gray800" textAlign="center">
                The keyboard adjusts the sheet on mobile devices, and the dollar
                formatting works correctly.
            </FootnoteRegular>
            <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                text="Confirm"
                onPress={() => {
                    onConfirm(dollarValue);
                }}
            />
        </YStack>
    );
};

/**
 * Inner component for the basic keyboard sheet example.
 * IMPORTANT: Local state must be managed in a separate component so the whole sheet
 * doesn't re-render on state change (if it does, it doesn't account for the keyboard size).
 */
const KeyboardSheetContent = ({
    onConfirm,
}: {
    onConfirm: (value: string) => void;
}) => {
    const [inputValue, setInputValue] = useState('');

    return (
        <YStack
            paddingVertical={60}
            paddingHorizontal={20}
            alignItems="center"
            gap={16}
        >
            <BodyLargeRegular>
                This is a keyboard compatible sheet.
            </BodyLargeRegular>
            <KeyboardCompatibleBottomSheetTextInput
                enablesReturnKeyAutomatically
                value={inputValue}
                onChangeText={(text: string) => {
                    setInputValue(text);
                }}
                placeholder="This is a text input."
            />
            <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                text="Close"
                onPress={() => {
                    onConfirm(inputValue);
                }}
            />
        </YStack>
    );
};

export const Sheets = () => {
    const [isStandardSheetOpen, setIsStandardSheetOpen] = useState(false);
    const [isKeyboardSheetOpen, setIsKeyboardSheetOpen] = useState(false);
    const [isTallSheetOpen, setIsTallSheetOpen] = useState(false);
    const [isTallSheetMinimalOpen, setIsTallSheetMinimalOpen] = useState(false);
    const [isTallSheetForKeyboardOpen, setIsTallSheetForKeyboardOpen] =
        useState(false);
    const [isKeyboardDollarSheetOpen, setIsKeyboardDollarSheetOpen] =
        useState(false);
    const [isSheetWithHeaderOpen, setIsSheetWithHeaderOpen] = useState(false);
    const [isSheetWithTitleBodyOpen, setIsSheetWithTitleBodyOpen] =
        useState(false);
    const [isSheetWithAllPropsOpen, setIsSheetWithAllPropsOpen] =
        useState(false);
    const [isPremiumInfoSheetOpen, setIsPremiumInfoSheetOpen] = useState(false);
    const closePremiumInfoSheet = () => {
        setIsPremiumInfoSheetOpen(false);
    };

    return (
        <YStack gap={16} width="100%" maxWidth={600} alignSelf="center">
            <BodyLargeRegular>Standard Sheets</BodyLargeRegular>
            <BodySmallRegular>
                The &quot;&lt;Sheet&gt;&quot; component uses the Tamagui Sheet
                component under the hood. It also automatically adapts to use
                &quot;&lt;SimpleModal&gt;&quot; on non-mobile web.
            </BodySmallRegular>
            <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.SMALL}
                text="Title & close button"
                onPress={() => {
                    setIsSheetWithHeaderOpen(true);
                }}
            />
            <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.SMALL}
                text="Title & body"
                onPress={() => {
                    setIsSheetWithTitleBodyOpen(true);
                }}
            />
            <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.SMALL}
                text="Header, title, body & children"
                onPress={() => {
                    setIsSheetWithAllPropsOpen(true);
                }}
            />
            <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.SMALL}
                text="Standard sheet"
                onPress={() => {
                    setIsStandardSheetOpen(true);
                }}
            />
            <BodyLargeRegular marginTop={24}>
                KeyboardCompatibleBottomSheets
            </BodyLargeRegular>
            <BodySmallRegular>
                &quot;KeyboardCompatibleBottomSheet&quot; is a nickname for the
                Gorhom Bottom Sheet component, which can be used directly as
                &quot;&lt;KeyboardCompatibleBottomSheet&gt;&quot; (not
                recommended), or forced via the &quot;&lt;Sheet
                forceKeyboardCompatibleBottomSheet&gt;&quot; (preferred).
            </BodySmallRegular>
            <BodySmallRegular>
                The Tamagui Sheet is (or can be) compatible with a native
                keyboard, but historically the Gorhom Bottom Sheet is (or at
                least was) better at it.
            </BodySmallRegular>
            <BodySmallRegular>
                It also automatically adapts to use
                &quot;&lt;SimpleModal&gt;&quot; on non-mobile web.
            </BodySmallRegular>
            <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.SMALL}
                text="Keyboard compatible sheet"
                onPress={() => {
                    setIsKeyboardSheetOpen(true);
                }}
            />
            <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.SMALL}
                text="Keyboard sheet with DollarInput component"
                onPress={() => {
                    setIsKeyboardDollarSheetOpen(true);
                }}
            />
            <BodyLargeRegular marginTop={24}>TallSheets</BodyLargeRegular>
            <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.SMALL}
                text="TallSheet with scroll"
                onPress={() => {
                    setIsTallSheetOpen(true);
                }}
            />
            <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.SMALL}
                text="TallSheet with minimal content"
                onPress={() => {
                    setIsTallSheetMinimalOpen(true);
                }}
            />
            <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.SMALL}
                text="TallSheet with Keyboard Compatibility"
                onPress={() => {
                    setIsTallSheetForKeyboardOpen(true);
                }}
            />
            <BodyLargeRegular marginTop={24}>PremiumInfoSheet</BodyLargeRegular>
            <BodySmallRegular>
                A floating, gold-branded card for Premium moments. Built on
                React Native&apos;s Modal rather than the bottom sheet, and
                always rendered in the light palette.
            </BodySmallRegular>
            <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.SMALL}
                text="Welcome to Premium"
                onPress={() => {
                    setIsPremiumInfoSheetOpen(true);
                }}
            />
            <PremiumInfoSheet
                isOpen={isPremiumInfoSheetOpen}
                onClose={closePremiumInfoSheet}
                title="Welcome to Fruitful Premium"
                listTitle="You’ve unlocked:"
                listItems={[
                    '4.00% APY on all cash',
                    '2% cash back on all purchases',
                    '0% AUM fees on investments',
                    'Your own dedicated Fruitful guide',
                ]}
                primaryAction={{
                    text: 'Let’s go!',
                    onPress: closePremiumInfoSheet,
                }}
                secondaryAction={{
                    text: 'See my benefits',
                    onPress: closePremiumInfoSheet,
                }}
            />
            <Sheet
                isOpen={isSheetWithHeaderOpen}
                onClose={() => {
                    setIsSheetWithHeaderOpen(false);
                }}
                title="Sheet Header"
                showCloseButton={true}
            >
                <BodyLargeRegular>
                    This sheet has a title and close button.
                </BodyLargeRegular>
            </Sheet>
            <Sheet
                isOpen={isSheetWithTitleBodyOpen}
                onClose={() => {
                    setIsSheetWithTitleBodyOpen(false);
                }}
                title="Information"
                showCloseButton={false}
            >
                <BodyMediumRegular>
                    This is the body text that explains something important. It
                    uses the proper typography and spacing from the Figma design
                    system.
                </BodyMediumRegular>
            </Sheet>
            <Sheet
                isOpen={isSheetWithAllPropsOpen}
                onClose={() => {
                    setIsSheetWithAllPropsOpen(false);
                }}
                title="Complete Example"
                showCloseButton={true}
                headerLeftAction={
                    <BackButton
                        iconType={BackButtonIconType.ARROW_LEFT}
                        customBackFn={() => {}}
                    />
                }
            >
                <YStack gap={16}>
                    <BodyMediumRegular>
                        This sheet demonstrates all the new props working
                        together. The header has a title and close button, the
                        content area has a title and children, plus additional
                        children below.
                    </BodyMediumRegular>
                    <YStack gap={12} paddingVertical={16}>
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            text="Primary Action"
                            onPress={() => {
                                setIsSheetWithAllPropsOpen(false);
                            }}
                        />
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.LARGE}
                            text="Secondary Action"
                            onPress={() => {
                                setIsSheetWithAllPropsOpen(false);
                            }}
                        />
                    </YStack>
                </YStack>
            </Sheet>
            <Sheet
                isOpen={isStandardSheetOpen}
                onClose={() => {
                    setIsStandardSheetOpen(false);
                }}
                showCloseButton={false}
            >
                <BodyLargeRegular marginBottom={60}>
                    🎈 This is a sheet.
                </BodyLargeRegular>
            </Sheet>
            {isKeyboardSheetOpen && (
                <KeyboardCompatibleBottomSheet
                    onDismiss={() => {
                        setIsKeyboardSheetOpen(false);
                    }}
                    showCloseButton={false}
                >
                    {/* Content is in a separate component to prevent re-renders from breaking keyboard handling */}
                    <KeyboardSheetContent
                        onConfirm={(value) => {
                            console.log('Keyboard input value:', value);
                            setIsKeyboardSheetOpen(false);
                        }}
                    />
                </KeyboardCompatibleBottomSheet>
            )}
            {/*
                Example: KeyboardCompatibleBottomSheet with DollarInput as component prop.

                This demonstrates using DollarInput with KeyboardCompatibleBottomSheetTextInput.

                IMPORTANT: Content is in a separate component (KeyboardDollarInputContent) because
                local state must be managed separately so the sheet doesn't re-render on state change.
                If the sheet re-renders, it doesn't account for the keyboard size and falls behind.
            */}
            {isKeyboardDollarSheetOpen && (
                <KeyboardCompatibleBottomSheet
                    title="Enter Amount"
                    onDismiss={() => {
                        setIsKeyboardDollarSheetOpen(false);
                    }}
                    showCloseButton={true}
                >
                    <KeyboardDollarInputContent
                        onConfirm={(value) => {
                            console.log('Keyboard dollar value:', value);
                            setIsKeyboardDollarSheetOpen(false);
                        }}
                    />
                </KeyboardCompatibleBottomSheet>
            )}
            <TallSheet
                isOpen={isTallSheetOpen}
                onClose={() => {
                    setIsTallSheetOpen(false);
                }}
                title="Terms and Conditions"
                showCloseButton={true}
            >
                <YStack gap={16}>
                    <BodyMediumRegular color="$red1100">
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                        Sed do eiusmod tempor incididunt ut labore et dolore
                        magna aliqua.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$orange1100">
                        Ut enim ad minim veniam, quis nostrud exercitation
                        ullamco laboris nisi ut aliquip ex ea commodo consequat.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$marigold1100">
                        Duis aute irure dolor in reprehenderit in voluptate
                        velit esse cillum dolore eu fugiat nulla pariatur.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$green1100">
                        Excepteur sint occaecat cupidatat non proident, sunt in
                        culpa qui officia deserunt mollit anim id est laborum.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$dropletBlue1100">
                        Sed ut perspiciatis unde omnis iste natus error sit
                        voluptatem accusantium doloremque laudantium.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$sweetViolet1100">
                        Totam rem aperiam, eaque ipsa quae ab illo inventore
                        veritatis et quasi architecto beatae vitae dicta sunt
                        explicabo.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$orchidPink1100">
                        Nemo enim ipsam voluptatem quia voluptas sit aspernatur
                        aut odit aut fugit.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$red1100">
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                        Sed do eiusmod tempor incididunt ut labore et dolore
                        magna aliqua.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$orange1100">
                        Ut enim ad minim veniam, quis nostrud exercitation
                        ullamco laboris nisi ut aliquip ex ea commodo consequat.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$marigold1100">
                        Duis aute irure dolor in reprehenderit in voluptate
                        velit esse cillum dolore eu fugiat nulla pariatur.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$green1100">
                        Excepteur sint occaecat cupidatat non proident, sunt in
                        culpa qui officia deserunt mollit anim id est laborum.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$dropletBlue1100">
                        Sed ut perspiciatis unde omnis iste natus error sit
                        voluptatem accusantium doloremque laudantium.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$sweetViolet1100">
                        Totam rem aperiam, eaque ipsa quae ab illo inventore
                        veritatis et quasi architecto beatae vitae dicta sunt
                        explicabo.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$orchidPink1100">
                        Nemo enim ipsam voluptatem quia voluptas sit aspernatur
                        aut odit aut fugit.
                    </BodyMediumRegular>
                </YStack>
            </TallSheet>
            <TallSheet
                isOpen={isTallSheetMinimalOpen}
                onClose={() => {
                    setIsTallSheetMinimalOpen(false);
                }}
                title="Minimal Content Example"
                showCloseButton={true}
            >
                <YStack gap={16}>
                    <BodyMediumRegular>
                        This TallSheet has minimal content but maintains the
                        same tall height as sheets with more content.
                    </BodyMediumRegular>
                    <BodyMediumRegular>
                        TallSheet always maintains consistent height.
                    </BodyMediumRegular>
                </YStack>
            </TallSheet>
            <TallSheet
                isOpen={isTallSheetForKeyboardOpen}
                onClose={() => {
                    setIsTallSheetForKeyboardOpen(false);
                }}
                title="TallSheet with Keyboard Compatibility"
                showCloseButton={true}
                forceKeyboardCompatibleBottomSheet={true}
            >
                <YStack gap={16}>
                    <BodyMediumRegular color="$red1100">
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                        Sed do eiusmod tempor incididunt ut labore et dolore
                        magna aliqua.
                    </BodyMediumRegular>
                    <Input placeholder="This is a text input." />
                    <BodyMediumRegular color="$orange1100">
                        Ut enim ad minim veniam, quis nostrud exercitation
                        ullamco laboris nisi ut aliquip ex ea commodo consequat.
                    </BodyMediumRegular>
                    <Input placeholder="This is a text input." />
                    <BodyMediumRegular color="$marigold1100">
                        Duis aute irure dolor in reprehenderit in voluptate
                        velit esse cillum dolore eu fugiat nulla pariatur.
                    </BodyMediumRegular>
                    <Input placeholder="This is a text input." />
                    <BodyMediumRegular color="$green1100">
                        Excepteur sint occaecat cupidatat non proident, sunt in
                        culpa qui officia deserunt mollit anim id est laborum.
                    </BodyMediumRegular>
                    <Input placeholder="This is a text input." />
                    <BodyMediumRegular color="$dropletBlue1100">
                        Sed ut perspiciatis unde omnis iste natus error sit
                        voluptatem accusantium doloremque laudantium.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$sweetViolet1100">
                        Totam rem aperiam, eaque ipsa quae ab illo inventore
                        veritatis et quasi architecto beatae vitae dicta sunt
                        explicabo.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$orchidPink1100">
                        Nemo enim ipsam voluptatem quia voluptas sit aspernatur
                        aut odit aut fugit.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$red1100">
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                        Sed do eiusmod tempor incididunt ut labore et dolore
                        magna aliqua.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$orange1100">
                        Ut enim ad minim veniam, quis nostrud exercitation
                        ullamco laboris nisi ut aliquip ex ea commodo consequat.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$marigold1100">
                        Duis aute irure dolor in reprehenderit in voluptate
                        velit esse cillum dolore eu fugiat nulla pariatur.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$green1100">
                        Excepteur sint occaecat cupidatat non proident, sunt in
                        culpa qui officia deserunt mollit anim id est laborum.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$dropletBlue1100">
                        Sed ut perspiciatis unde omnis iste natus error sit
                        voluptatem accusantium doloremque laudantium.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$sweetViolet1100">
                        Totam rem aperiam, eaque ipsa quae ab illo inventore
                        veritatis et quasi architecto beatae vitae dicta sunt
                        explicabo.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$orchidPink1100">
                        Nemo enim ipsam voluptatem quia voluptas sit aspernatur
                        aut odit aut fugit.
                    </BodyMediumRegular>
                </YStack>
            </TallSheet>
        </YStack>
    );
};
